import type { DataTypeLocator } from '@helo/cms'
import {
  DataTypeConfig,
  DataTypeConfigUtils,
  FilterCondition,
  InputObjectDataTypeConfig,
  InputValueContentNode,
  InputValueDataTypeConfig,
} from '@helo/cms'
import { ContentNodeUtils } from '@helo/cms'
import { Assert, List, Value } from '@helo/utils'
import { pipe } from 'effect'
import { useMemo } from 'react'

import { useAppStore } from '../../../store/appStore/appStore'
import {
  isAnalyticsDateFilterTypeName,
  isDateFilterTypeName,
} from '../dateFilterInputAdapters'

/** Click-outside boundary ID for the filter editor popover; date/range calendars use this so selecting a date doesn't close the popover. */
export const FILTER_EDITOR_BOUNDARY_ID = 'filter-editor-popover'

/** Check if a filter field has a value (used by FilterEditor and FilterChipsEditor) */
export const hasFilterValue = (data: unknown): boolean => {
  if (data === undefined || data === null) return false

  if (typeof data === 'object') {
    const dataObject = data as Record<string, unknown>
    const notObject = dataObject.not as Record<string, unknown> | undefined

    // Check for blank/notBlank conditions explicitly (null is a valid filter value)
    if (dataObject.equals === null || notObject?.equals === null) return true

    // Check for other active filter values (exclude empty string - e.g. number filter with no value)
    const hasMeaningfulValue = (v: unknown) =>
      v !== undefined && v !== null && v !== ''

    return Object.entries(dataObject).some(([key, value]) => {
      if (
        key === 'not' &&
        value &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        return Object.values(value as Record<string, unknown>).some(
          nestedValue => hasMeaningfulValue(nestedValue),
        )
      }
      if (Array.isArray(value)) {
        return value.length > 0 && value.some(hasMeaningfulValue)
      }
      return hasMeaningfulValue(value)
    })
  }

  return data !== ''
}

/** True when the filter value type is an analytics (cube-style) date filter. */
export const isCubeDateTimeFilterConfig = (
  config: { ofType?: { typeName?: string } } | undefined | null,
): boolean => isAnalyticsDateFilterTypeName(config?.ofType?.typeName)

/** True if the filter field config is date-type. Used to clear value when switching date → non-date. */
export const isDateTimeFilterConfig = (
  config: { ofType?: { typeName?: string } } | undefined | null,
): boolean => isDateFilterTypeName(config?.ofType?.typeName)

/** Map of filter condition keys to display labels */
export const CONDITION_LABELS: Record<string, string> = {
  contains: 'Contains',
  equals: 'Equals',
  gt: 'Is Greater Than',
  gte: 'Is Greater Than Or Equals',
  lt: 'Is Less Than',
  lte: 'Is Less Than Or Equals',
  in: 'Is Any Of',
  inList: 'In List',
  notIn: 'Not In',
  inRange: 'In Range',
  startsWith: 'Starts With',
  endsWith: 'Ends With',
  blank: 'Is Blank',
  notBlank: 'Is Present',
  notEqual: 'Does Not Equal',
  notContains: 'Does Not Contain',
}

/** Condition values that are number-only; must not appear in StringFilterEditor dropdown. */
export const NUMBER_ONLY_CONDITION_VALUES = new Set([
  'gt',
  'gte',
  'lt',
  'lte',
  'inRange',
])

/** Order for string filter condition dropdown. Synthetic options (inList, blank, notBlank, notContains, notEqual) are added in FilterEditorRow when not from schema. */
export const STRING_CONDITION_ORDER: string[] = [
  'in', // Is Any Of
  'contains',
  'inList', // In List (text input for multiple values)
  'equals',
  'blank', // Is Blank
  'notBlank', // Is Present
  'startsWith',
  'endsWith',
  'notContains', // Does Not Contain
  'notEqual', // Does Not Equal
]

/** Synthetic string conditions not top-level in schema (blank=equals:null, notBlank=not.equals:null, notContains=not.contains, notEqual=not.equals, inList=same as in with text input) */
export const STRING_EXTRA_CONDITION_OPTIONS: Array<{
  label: string
  value: string
  isArray?: boolean
  requiresValue?: boolean
}> = [
  { label: CONDITION_LABELS.inList, value: 'inList', isArray: true },
  { label: CONDITION_LABELS.blank, value: 'blank', requiresValue: false },
  { label: CONDITION_LABELS.notBlank, value: 'notBlank', requiresValue: false },
  { label: CONDITION_LABELS.notContains, value: 'notContains' },
  { label: CONDITION_LABELS.notEqual, value: 'notEqual' },
]

/** String condition options in display order. Single source for stories and any consumer that needs the full list without schema. */
export const STRING_CONDITION_OPTIONS: Array<{
  label: string
  value: string
  isArray?: boolean
  requiresValue?: boolean
}> = STRING_CONDITION_ORDER.map(key => {
  const extra = STRING_EXTRA_CONDITION_OPTIONS.find(o => o.value === key)
  return (
    extra ?? {
      label: CONDITION_LABELS[key] ?? key,
      value: key,
      isArray: key === 'in' || key === 'inList',
      requiresValue: key !== 'blank' && key !== 'notBlank',
    }
  )
})

/** Order for number/float filter condition dropdown (matches design). Excludes In List / Not In. */
export const NUMBER_CONDITION_ORDER: string[] = [
  'equals',
  'notEqual',
  'blank',
  'notBlank',
  'gt',
  'gte',
  'lt',
  'lte',
  'inRange',
]

/** Synthetic number conditions not exposed as top-level schema fields (notEqual=not.equals, blank=equals:null, notBlank=not.equals:null, inRange=gte+lte) */
export const NUMBER_EXTRA_CONDITION_OPTIONS: Array<{
  label: string
  value: string
  isArray?: boolean
  requiresValue?: boolean
}> = [
  { label: CONDITION_LABELS.inRange, value: 'inRange', isArray: true },
  { label: CONDITION_LABELS.notEqual, value: 'notEqual' },
  { label: CONDITION_LABELS.blank, value: 'blank', requiresValue: false },
  { label: CONDITION_LABELS.notBlank, value: 'notBlank', requiresValue: false },
]

/** Re-export for FilterEditorRow and FilterChipsEditor; defined in dateFilterConditionOptions.ts to avoid circular imports. */
export {
  CUBE_DATE_FILTER_CONDITION_OPTIONS,
  DATE_FILTER_CONDITION_OPTIONS,
  DATE_FILTER_CONDITION_OPTIONS_EXTENDED,
} from '../dateFilterConditionOptions'

/** Option shape for "Is Any Of" pick list (distinct values / load options). */
export type PickListOption = { label: string; value: string }

/** Params for the distinct values / pick list load function. */
export type LoadOptionsForPickListParams = {
  unmappedTypeName?: string
  unmappedFieldKey?: string
  search: string
  limit?: number
  notIn?: string[]
  skip?: number
  currentFieldKey?: string
  unmappedPath?: Array<[string, string]>
  rootFilter?: Record<string, unknown>
  /**
   * Cube member / view name for cube-native distinct typeahead
   * (e.g. `container_list_view`, `transit_time_analysis`).
   * When set, pick-list options load from cube instead of Prisma distinctValues.
   */
  cubeDistinctView?: string
}

/** Async load options for "Is Any Of" pick list. Used by FilterEditor and FilterEditorRow. */
export type LoadOptionsForPickListFn = (
  params: LoadOptionsForPickListParams,
) => Promise<PickListOption[]>

/** Config shape that has meta.unmappedPath (e.g. filter input type config). */
type ConfigWithUnmappedPath = {
  meta?: { unmappedPath?: DataTypeLocator }
  state?: Record<string, unknown>
}

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const getPathEntries = (
  path: DataTypeLocator | undefined,
  tag: string,
): string[] => {
  if (!path || !Array.isArray(path)) return []
  return path.flatMap(entry =>
    Array.isArray(entry) &&
    entry.length === 2 &&
    entry[0] === tag &&
    typeof entry[1] === 'string'
      ? [entry[1]]
      : [],
  )
}

const getUnmappedKeyPath = (
  config: ConfigWithUnmappedPath | undefined,
): string[] => getPathEntries(config?.meta?.unmappedPath, 'key')

const buildNestedFilterFromPath = (
  path: string[],
  value: Record<string, unknown>,
): Record<string, unknown> => {
  if (path.length === 0) return value

  return path.reduceRight<Record<string, unknown>>(
    (acc, key) => ({ [key]: acc }),
    value,
  )
}

const mergeDeep = (
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): Record<string, unknown> => {
  const merged = { ...target }

  for (const [key, value] of Object.entries(source)) {
    const existing = merged[key]
    merged[key] =
      isObjectRecord(existing) && isObjectRecord(value)
        ? mergeDeep(existing, value)
        : value
  }

  return merged
}

/**
 * Gets the unmapped type name from a data type config's meta.unmappedPath.
 * For a filter object use this (first typeName). For a field use getUnmappedTypeNameForField.
 */
export const getUnmappedTypeName = (
  config: ConfigWithUnmappedPath | undefined,
): string | undefined =>
  getPathEntries(config?.meta?.unmappedPath, 'typeName')[0]

/**
 * Gets the unmapped type name that contains this field (last typeName in path).
 * Use for distinct query: nested fields (e.g. carrier under ShipmentDocument) live on a different
 * FilterInput type (ShipmentDocumentFilterInput) than the root (TransportUnitShipmentDocumentFilterInput).
 *
 * Prefer `state.filterDistinctInputType` when set (e.g. cube dashboard fields that map to a
 * Prisma distinct source like LaneFilterInput / ShipmentDocumentFilterInput).
 */
export const getUnmappedTypeNameForField = (
  config: ConfigWithUnmappedPath | undefined,
): string | undefined => {
  const stateType = config?.state?.filterDistinctInputType
  if (typeof stateType === 'string' && stateType !== '') {
    return stateType
  }
  // @ts-expect-error - at is not a function on string[]
  return getPathEntries(config?.meta?.unmappedPath, 'typeName').at(-1)
}

/**
 * True when the type name is a Prisma `*FilterInput` eligible for
 * `electrumpython.distinctValues` (cube `*WhereInput` types are not).
 */
export const isDistinctValuesFilterInputType = (
  typeName: string | undefined | null,
): boolean =>
  typeof typeName === 'string' &&
  typeName.endsWith('FilterInput') &&
  !typeName.endsWith('NestedListFilterInput')

/**
 * Gets the unmapped field key from a data type config's meta.unmappedPath.
 * Distinct values API expects the schema field name (e.g. shipmentDocument not carrier).
 */
export const getUnmappedFieldKey = (
  config: ConfigWithUnmappedPath | undefined,
): string | undefined => {
  const keyPath = getUnmappedKeyPath(config)
  return keyPath.length > 0 ? keyPath[keyPath.length - 1] : undefined
}

export const getDistinctValueFieldKey = (
  config: ConfigWithUnmappedPath | undefined,
): string | undefined => {
  const stateValue = config?.state?.filterDistinctValueKey
  if (typeof stateValue === 'string' && stateValue !== '') {
    return stateValue
  }

  return getUnmappedFieldKey(config)
}

/**
 * Cube member name for native distinct typeahead (`state.cubeDistinctView`).
 * e.g. `container_list_view` — pick-list loads via cube contains, not Prisma.
 */
export const getCubeDistinctView = (
  config: ConfigWithUnmappedPath | undefined,
): string | undefined => {
  const view = config?.state?.cubeDistinctView
  return typeof view === 'string' && view !== '' ? view : undefined
}

export const getUnmappedPath = (
  config: ConfigWithUnmappedPath | undefined,
): Array<[string, string]> | undefined => {
  const path = config?.meta?.unmappedPath
  if (!path || !Array.isArray(path)) return undefined
  return path as Array<[string, string]>
}

export const buildDistinctRootFilter = (
  fields: InputValueContentNode[],
  excludedFieldKeys: string[] = [],
): Record<string, unknown> => {
  const excludedKeys = new Set(excludedFieldKeys.filter(Boolean))

  return fields.reduce<Record<string, unknown>>((acc, field) => {
    if (
      !field?.key ||
      field.key === '' ||
      excludedKeys.has(field.key) ||
      !hasFilterValue(field.data)
    ) {
      return acc
    }

    const config = field.getDataTypeConfig() as ConfigWithUnmappedPath
    const keyPath = getUnmappedKeyPath(config)
    if (!isObjectRecord(field.data)) {
      return acc
    }

    return mergeDeep(acc, buildNestedFilterFromPath(keyPath, field.data))
  }, {})
}

export const getAvailableFilterFieldsDataTypeConfigs = (
  dataTypeConfig: InputObjectDataTypeConfig,
  hasRole: (roles?: string[] | undefined) => boolean,
): InputValueDataTypeConfig<string, DataTypeConfig>[] => {
  return pipe(
    dataTypeConfig,
    DataTypeConfigUtils.getFields(),
    List.filter(
      (field): field is InputValueDataTypeConfig<string, DataTypeConfig> =>
        field.state.filterDisabled !== true && hasRole(field.state.roles),
    ),
  )
}

export const useAvailableFilterFieldsDataTypeConfigs = (
  dataTypeConfig: InputObjectDataTypeConfig,
) => {
  const hasRole = useAppStore(store => store.auth.hasRole)

  return useMemo(
    () => getAvailableFilterFieldsDataTypeConfigs(dataTypeConfig, hasRole),
    [dataTypeConfig, hasRole],
  )
}

export const getFilterObjectContentNode = (
  inputValue: InputValueContentNode,
) => {
  return pipe(
    inputValue,
    Assert.orThrow(
      Value.isNotNullable,
      () => new Error('Filter input value is null'),
    ),
    ContentNodeUtils.getOfType(),
    Assert.orThrow(
      ContentNodeUtils.isKind('INPUT_OBJECT'),
      () => new Error('Filter input value is not an input object'),
    ),
  )
}

export const updateFilterInputValue = (
  newFieldContentNode: InputValueContentNode,
  filterInputValue: InputValueContentNode,
) =>
  pipe(
    newFieldContentNode,
    ContentNodeUtils.updateParentContentNode(
      getFilterObjectContentNode(filterInputValue!),
    ),
    ContentNodeUtils.updateParentContentNode(filterInputValue),
    Assert.orThrow(
      ContentNodeUtils.isKind('INPUT_VALUE'),
      () => new Error('Expected INPUT_VALUE'),
    ),
  )

/** Builds merged filter data from draft editor rows (only rows with meaningful values). */
export const buildMergedFilterDataFromRows = (
  fields: InputValueContentNode[],
): Record<string, unknown> => {
  const merged: Record<string, unknown> = {}

  for (const node of fields) {
    if (!node) continue
    const key = node.key
    if (!key || key === '') continue
    if (hasFilterValue(node.data)) {
      merged[key] = node.data
    }
  }

  return merged
}

/** Derives applied filter state from persisted filter input value data. */
export const buildAppliedMergedFilterData = (
  filterData: unknown,
): Record<string, unknown> => {
  if (
    !filterData ||
    typeof filterData !== 'object' ||
    Array.isArray(filterData)
  ) {
    return {}
  }

  const merged: Record<string, unknown> = {}

  for (const [fieldKey, fieldValue] of Object.entries(
    filterData as Record<string, unknown>,
  )) {
    if (!fieldKey) continue
    if (hasFilterValue(fieldValue)) {
      merged[fieldKey] = fieldValue
    }
  }

  return merged
}

/** True when draft merged filter data differs from applied filter data. */
export const hasFilterEditorApplyChanges = (
  current: Record<string, unknown>,
  applied: Record<string, unknown>,
): boolean => {
  const currentKeys = Object.keys(current).sort()
  const appliedKeys = Object.keys(applied).sort()

  if (currentKeys.length !== appliedKeys.length) return true

  for (const key of currentKeys) {
    if (!(key in applied)) return true
    if (JSON.stringify(current[key]) !== JSON.stringify(applied[key])) {
      return true
    }
  }

  return false
}

/** Returns row indexes that fail validation before applying filter changes. */
export const validateFilterEditorRows = (
  fields: InputValueContentNode[],
  appliedData: Record<string, unknown>,
): Set<number> => {
  const errors = new Set<number>()

  for (let index = 0; index < fields.length; index++) {
    const node = fields[index]
    if (!node) continue
    const key = node.key
    if (!key || key === '') continue
    if (key in appliedData && !hasFilterValue(node.data)) continue
    if (!hasFilterValue(node.data)) {
      errors.add(index)
    }
  }

  return errors
}

/** True when a single filter field's draft data differs from its applied data. */
export const hasFilterFieldDataChanges = (
  current: unknown,
  applied: unknown,
): boolean => JSON.stringify(current) !== JSON.stringify(applied)
